import { IGroupPaymentInfo, IOrder } from '@/interfaces/IOrder';
import { IOrderItem } from '@/interfaces/IOrderItem';
import { IPrintingSettings } from '@/interfaces/IPrintingSettings';
import { classifyGroupTicketMember } from './auto-print-policy';

/**
 * Pure resolver for the full-selection group payment ticket: turns the
 * transient `groupPayment.orderIds` marker (the FULL selection) into a print
 * plan — which orders the document renders (`docOrders`), which of them are
 * receipted/marked (`receipt`), and the cross-device dedupe key.
 *
 * All dependencies are injected so the module stays pure and unit-testable.
 * Resolution boundary (accepted by design): membership resolution is
 * store-first and NEVER fetches over the network inside the print path.
 * Orders evicted from the pending orders store are warn-skipped and the
 * document degrades to the resolvable members. Seat/table orders — the flow
 * this feature targets — stay in the store while the table is open even when
 * Paid+Finished, so the primary flow always resolves.
 */

export interface IGroupPrintReceiptEntry {
  order: IOrder;
  items: IOrderItem[];
  mode: 'all' | 'unprinted';
}

export interface IGroupPrintPlan {
  /** Receipt ∪ context orders, ascending by id (deterministic numbering). */
  docOrders: IOrder[];
  /** The only members that are printed-as-receipt and marked/stamped. */
  receipt: IGroupPrintReceiptEntry[];
  dedupeKey: string;
}

export function resolveGroupPrintPlan({
  group,
  capturedMembers,
  findLiveOrder,
  mergePrintedCache,
  getFilteredTicketItems,
  settings,
  deviceId,
}: {
  group: IGroupPaymentInfo;
  /** WS-captured members: resolution fallback for store-evicted orders. */
  capturedMembers: IOrder[];
  findLiveOrder: (id: number) => IOrder | undefined;
  mergePrintedCache: (order: IOrder) => void;
  getFilteredTicketItems: (order: IOrder) => IOrderItem[];
  settings: Pick<IPrintingSettings, 'trigger' | 'source'>;
  deviceId: string | null | undefined;
}): IGroupPrintPlan | null {
  const capturedById = new Map(capturedMembers.map((o) => [o.id, o]));

  const contextOrders: IOrder[] = [];
  const receipt: IGroupPrintReceiptEntry[] = [];

  for (const id of [...group.orderIds].sort((a, b) => a - b)) {
    const order = findLiveOrder(id) ?? capturedById.get(id);

    if (!order) {
      // Degrades to the resolvable members — never block, never fetch.
      console.warn(
        `[GroupPrintPlan] Order ${id} of payment ${group.paymentId} not resolvable, skipping`
      );
      continue;
    }

    // printGroup → ensureOrderPrintable throws on orders missing items or
    // fields; one degenerate context member must never sink the whole
    // document into a retryable failure.
    if (!order.orderItems || typeof order.orderFields === 'undefined') {
      console.warn(
        `[GroupPrintPlan] Order ${id} of payment ${group.paymentId} lacks items/fields, skipping`
      );
      continue;
    }

    mergePrintedCache(order);

    const classification = classifyGroupTicketMember({
      order,
      settings,
      deviceId,
    });

    if (classification.role === 'exclude') continue; // dropped from doc AND totals

    if (classification.role === 'context') {
      contextOrders.push(order);
      continue;
    }

    const items =
      classification.mode === 'all'
        ? order.orderItems
        : getFilteredTicketItems(order);

    if (!items.length) {
      // Nothing left to receipt: still render for correct totals.
      contextOrders.push(order);
      continue;
    }

    receipt.push({ order, items, mode: classification.mode });
  }

  // No receipt members: the replay / duplicate-delivery no-op path.
  if (receipt.length === 0) return null;

  const docOrders = [...receipt.map((r) => r.order), ...contextOrders].sort(
    (a, b) => a.id - b.id
  );

  // Same shape as the previous per-subset key, now over the full document —
  // devices assembling the same document collapse on a shared bridge.
  const dedupeKey = `auto:group:${group.paymentId}:${docOrders
    .map((o) => o.id)
    .join('.')}`;

  return { docOrders, receipt, dedupeKey };
}
